import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { KeycloakService, KeycloakStoreService, SKIP_KEYCLOAK_INTERCEPTOR } from '@ogs/ngx-keycloak';
import { IMAGE_FORMATS, parseBase64ToImage } from '@shared/components/table-implementation/functions/base64-image-parse';
import { ParsedKeycloakToken } from 'node_modules/@ogs/ngx-keycloak/types/token.type';
import { catchError, EMPTY, map, Observable, of, Subject, tap, throwError } from 'rxjs';
import { isSetPasswordToken, SET_PASSWORD_TOKENS } from '../types/set-password-tokens';
import { ValidationResponse } from '@shared/types/api-validation-response';
import { CORE_ROUTES } from '@core/constants/routes.constant';
import { AUTHENTICATE_ROUTES } from '../constants/routes.constant';
import { Router } from '@angular/router';
import { CACHED_EMAIL_REMEMBER, CACHED_EMAIL_RESET } from '@core/constants/cache.constant';
import { REGISTER_USER_TOKENS } from '../types/register-user-tokens';
import { SetPasswordResponse } from '../types/set-password-response';
import { RegisterUserResponse } from '../types/register-user-response';
import { User } from '../types/user';
import { WaitCursorService } from "@core/services/wait-cursor.service";
import { IdleService } from '@core/services/idle.service';


interface PasswordExpirationResponse {
  expiryDate: Date;
  daysRemaining: number;
}

interface UserResponse {
  name: {
    first: string;
    last: string;
  };
  number: string;
  position_name: string;
  status: {
    anonymize: boolean;
    blocked: boolean;
    complete: boolean;
  };
  userImage: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private _httpClient: HttpClient = inject<HttpClient>(HttpClient);

  private _keycloakStoreService: KeycloakStoreService = inject(KeycloakStoreService);

  private _keycloakService: KeycloakService = inject(KeycloakService);

  private _waitCursorService: WaitCursorService = inject(WaitCursorService);

  private _idleService: IdleService = inject(IdleService);

  private _router: Router = inject(Router);

  private _cachedEmailForPasswordReset: string | undefined;

  private _login$: Subject<void> = new Subject<void>();

  public login$: Observable<void> = this._login$.asObservable();

  private set cachedEmailForPasswordReset (email: string) {
    window.localStorage.setItem(CACHED_EMAIL_RESET, email);
    this._cachedEmailForPasswordReset = email;
  }

  private get cachedEmailForPasswordReset (): string {
    if (!this._cachedEmailForPasswordReset)
      this._cachedEmailForPasswordReset = window.localStorage.getItem(CACHED_EMAIL_RESET) ?? "";

    return this._cachedEmailForPasswordReset;
  }


  public sendPasswordResetMail (email: string, isUuid?: boolean, operation?: string): Observable<void> {
    // eslint-disable-next-line @tseslint/typedef
    const options = {
      headers: new HttpHeaders().append(SKIP_KEYCLOAK_INTERCEPTOR, "true")
    };

    return this._httpClient.post<undefined>(`/api/administration/authorization/users/register-access?${ isUuid ? 'userId=' : 'eMail=' }${ encodeURIComponent(email) }${ operation ? `&operation=${ operation }` : '' }`, {}, options).pipe(catchError(() => EMPTY));
  }

  /**
   * Todo: is a cached mail safe and best practice?
   *
   * Sets a new password for the given user.
   * If no user is provided, looks for email that is cached when password of user is expired
   * If validation fails, returns a validation token if available.
   *
   * @param newPassword - The new password to set.
   * @param newPasswordRepeat - Confirmation of the new password.
   * @param username - The username of the user (optional; looks for cached email when not provided).
   * @param currentPassword - The current password (optional).
   * @returns An Observable emitting a `SetPasswordResponse` indicating success or a validation token on failure.
   */
  public changePassword (newPassword: string, newPasswordRepeat: string, username: string = "", currentPassword: string = ""): Observable<SetPasswordResponse> {
    let email: string = username;

    if (email === "" && this.cachedEmailForPasswordReset)
      email = this.cachedEmailForPasswordReset;

    // eslint-disable-next-line @tseslint/typedef
    const options = {
      headers: new HttpHeaders().append(SKIP_KEYCLOAK_INTERCEPTOR, "true")
    };


    return this._httpClient.put<ValidationResponse>("/api/administration/user/interactions", {
      username: email,
      newPassword,
      newPasswordRepeat,
      currentPassword
    }, options)
      .pipe(
        tap(() => {
          this.cachedEmailForPasswordReset = "";
        }),
        map(() => ({ hasWorked: true })),
        catchError((resp: HttpErrorResponse) => {
          const setPasswordResponse: SetPasswordResponse = { hasWorked: false };
          const validation: ValidationResponse | null = resp.error as ValidationResponse | null;

          if (validation?.messages && validation.messages.length > 0) {
            // eslint-disable-next-line @tseslint/no-non-null-assertion
            const token: string = validation.messages[ 0 ]!.token;

            if (isSetPasswordToken(token))
              setPasswordResponse.passwordValidationToken = token;
          }

          return of(setPasswordResponse);
        })
      );
  }

  /**
   * Logs out the current user and navigates to the login page.
   *
   * @param returnPath - Optional path were the router should navigate after logout. Navigates to login on default.
   */
  public logout (returnPath: string = `${ CORE_ROUTES.AUTHENTICATE }/${ AUTHENTICATE_ROUTES.LOGIN }`): void {
    this._keycloakService.logout().subscribe({
      next: (): void => {
        void this._router.navigateByUrl(returnPath);
      }
    });
  }

  /**
   * Attempts to log in a user with the provided email and password.
   * On a 400 response (password expired), caches the email and navigates to the password-expired page.
   * On success, optionally remembers the email in localStorage, and navigates to the projects page.
   *
   * @param email - The user's email address.
   * @param password - The user's password.
   * @param shouldRememberEmail - If true, stores the email in localStorage; otherwise removes any stored email.
   * @returns An Observable that completes on successful login or errors for non-400 HTTP failures.
   */
  public login (email: string, password: string, shouldRememberEmail?: boolean): Observable<void> {
    return this._keycloakService.login(email, password).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 400) {
          this.cachedEmailForPasswordReset = email;
          void this._router.navigateByUrl(`${ CORE_ROUTES.AUTHENTICATE }/${ AUTHENTICATE_ROUTES.PASSWORD_EXPIRED }`);

          return EMPTY;
        }

        return throwError(() => error);
      }),
      tap(() => {
        if (shouldRememberEmail) window.localStorage.setItem(CACHED_EMAIL_REMEMBER, email); else window.localStorage.removeItem(CACHED_EMAIL_REMEMBER);

        const path: string = this._idleService.getIdlePath() ?? CORE_ROUTES.PROJECTS;

        void this._router.navigateByUrl(path)
          .then(() => {
            // TODO?: is there a better way to separate concerns
            this._idleService.clearIdlePath();
          });
        this._login$.next();
      }),
      // eslint-disable-next-line @unicorn/no-useless-undefined
      map(() => undefined)
    );
  }

  /**
   * Registers a user using a Base64-encoded key. If `passwordCredentials` is provided,
   * sends a POST to set a new password; otherwise, sends a GET to complete registration.
   *
   * @param registerKey - A Base64 string decoding to "keycloakId;timeStamp;uuid" (from the password-reset link).
   * @param passwordCredentials - An optional object with `newPassword` and `newPasswordRepeat` for setting a password.
   * @returns Observable yielding `{ isRegistered: true }` on success; on failure, `{ isRegistered: false, errorToken }`
   *   where `errorToken` identifies the validation error.
   */
  registerUser (registerKey: string, passwordCredentials?: {
    newPassword: string;
    newPasswordRepeat: string;
  }): Observable<RegisterUserResponse> {
    let encodedKey: string;

    const registerUserResp: RegisterUserResponse = { isRegistered: false, errorToken: undefined };


    try {
      encodedKey = window.atob(registerKey);
    } catch {
      return of(registerUserResp);
    }

    const values: string[] = encodedKey.split(";");

    if (values.length === 3) {
      const url: string = `/api/administration/authorization/users/register`;
      const params: HttpParams = new HttpParams()
      // eslint-disable-next-line @tseslint/no-non-null-assertion
        .set("keycloakId", values[ 0 ]!)
      // eslint-disable-next-line @tseslint/no-non-null-assertion
        .set("timeStamp", values[ 1 ]!)
      // eslint-disable-next-line @tseslint/no-non-null-assertion
        .set("uuid", values[ 2 ]!);
      // eslint-disable-next-line @tseslint/typedef
      const httpOptions = { params, headers: new HttpHeaders().append(SKIP_KEYCLOAK_INTERCEPTOR, "true") };
      const registerUser: Observable<object> = passwordCredentials
        ? this._httpClient.post(url, {
          password: passwordCredentials.newPassword,
          verifyPassword: passwordCredentials.newPasswordRepeat
        }, httpOptions)
        : this._httpClient.get(url, httpOptions);


      return registerUser.pipe(
        map(() => {
          registerUserResp.isRegistered = true;

          return registerUserResp;
        }),
        catchError((resp: HttpErrorResponse) => {
          const validation: ValidationResponse | null = resp.error as ValidationResponse | null;

          if (validation?.messages && validation.messages.length > 0) {
            // eslint-disable-next-line @tseslint/no-non-null-assertion
            const token: string = validation.messages[ 0 ]!.token;

            registerUserResp.errorToken = token;
          } else if (resp.status === 400)
            registerUserResp.errorToken = SET_PASSWORD_TOKENS.PASSWORD_POLICY_ERROR;
          else if (resp.status === 404)
            registerUserResp.errorToken = REGISTER_USER_TOKENS.INVALID_REGISTRATION_LINK;

          return of(registerUserResp);
        })
      );
    }

    registerUserResp.errorToken = REGISTER_USER_TOKENS.INVALID_REGISTRATION_LINK;

    return of(registerUserResp);
  }


  /**
   * Checks if the password of a user has expired.
   *
   * @param email - The user's email address.
   * @returns An observable whether the password is expired or not.
   */
  public isPasswordExpired (email: string): Observable<boolean> {
    let params: HttpParams = new HttpParams();

    params = params.set("eMail", email);

    return this._httpClient.get<PasswordExpirationResponse>("api/administration/authorization/users/password-expiry-date", { params })
      .pipe(map((resp: PasswordExpirationResponse) => resp.daysRemaining === 0), catchError(() => of(false)));
  }

  /**
   * Retrieves the currently logged-in user with detailed profile information.
   * Falls back to token-based data if HTTP request fails.
   *
   * @returns An observable of the user object or undefined if not logged in.
   */
  public getRegisteredUser (): Observable<User | undefined> {
    const token: ParsedKeycloakToken | null = this._keycloakStoreService.parsedAccessToken;

    return token
      ? this._httpClient
        .get<UserResponse>(
          `/api/customers/*/employees/users/${ token.email }`
        )
        .pipe(
          map((resp: UserResponse) => ({
            email: token.email,
            job: resp.position_name,
            firstName: resp.name.first,
            lastName: resp.name.last,
            name: `${ resp.name.first } ${ resp.name.last }`,
            userImg: parseBase64ToImage(resp.userImage, IMAGE_FORMATS.png)
          })),
          catchError((): Observable<User> => of({
            email: token.email,
            name: token.name,
            firstName: token.given_name,
            lastName: token.family_name,
            job: "",
            userImg: ""
          }))
        )
      : of();
  }

  /**
   * Navigates to Login-Page
   */
  navigateToLogin (): void {
    void this._router.navigateByUrl(`${ CORE_ROUTES.AUTHENTICATE }/${ AUTHENTICATE_ROUTES.LOGIN }`);
  }

  /**
   * Navigates to Forgot-Password-Page
   *
   * @param referrer page to which forgot-password can return (optional; default is Login)
   */
  navigateToForgotPassword (referrer: string = `${ CORE_ROUTES.AUTHENTICATE }/${ AUTHENTICATE_ROUTES.LOGIN }`): void {
    const params: URLSearchParams = new URLSearchParams();
    const encodedReferrer: string = encodeURI(referrer);

    params.append("referrer", encodedReferrer);
    void this._router.navigateByUrl(`${ CORE_ROUTES.AUTHENTICATE }/${ AUTHENTICATE_ROUTES.FORGOT_PASSWORD }?${ params.toString() }`);
  }
}

